import {
  ePlanCode,
  ePlanFrequency,
  eSubscriptionStatus,
} from './Subscription.enums';
import {
  tSubscriptionContext,
  tSubscriptionSummary,
} from './Subscription.types';

import { BASE_URL_BILLING_SERVICE } from 'src/axios/axios.constants.ts';

export const ENDPOINT_BILLING_SERVICE = BASE_URL_BILLING_SERVICE;

export const INITIAL_SUBSCRIPTION_VALUES: tSubscriptionSummary = {
  currency: '',
  totalAmount: 0,
  status: eSubscriptionStatus.INACTIVE,
  pendingCancellation: false,
  endDate: new Date(),
  defaultPaymentMethod: {
    lastFourDigits: '',
    cardNetwork: '',
  },
  notifications: [],
  frequency: ePlanFrequency.MONTHLY,
  includedSeats: 0,
  purchasedSeats: 0,
  purchasedSeatsCost: 0,
  subscriptionBaseCost: 0,
  addons: {
    apiKey: false,
    chatbot: false,
  },
  features: [],
  name: null,
  plan_type: '',
  trialDays: null,
  planCode: ePlanCode.PLAN_LEGACY,
  planCredits: 0,
  startDate: new Date(),
  customerId: '',
  // RET-1269: los 17 campos de mora, sembrados en `null` — «no sé» honesto.
  // NINGUNO arranca en 0 ni en '': `daysPastDue: 0` sería «primer día de mora»
  // y `amountRemaining: 0` sería «no debe nada», dos afirmaciones inventadas
  // sobre un workspace del que todavía no llegó el resumen. Es la misma lección
  // que obligó a existir a `hasSubscriptionData` (RET-1908).
  pastDueSince: null,
  daysPastDue: null,
  pastDueAnchorSource: null,
  cancellationDate: null,
  cancellationDateSource: null,
  amountRemaining: null,
  nextPaymentAttempt: null,
  dueDate: null,
  hostedInvoiceUrl: null,
  attempted: null,
  attemptCount: null,
  collectionMethod: null,
  openInvoiceSource: null,
  declineCode: null,
  adviceCode: null,
  cardBrand: null,
  cardLast4: null,
  debtInvoices: null,
  hostedPaymentUrl: null,
  lastDecline: null,
  lastDeclineSource: null,
  planPause: null,
  outboundMessagingAccess: true,
};

/**
 * Las cuatro fronteras de día que consumen los tres modales de mora heredados:
 * `[dayStart, dayMid, dayPrev, dayLast]`.
 *
 * Salían de `Number(import.meta.env.VITE_…) || 5` y de un memo que construía
 * `[0, floor(n/2), n-1, n]` y lo deduplicaba; con el valor desplegado en todos
 * los entornos (5) eso da exactamente `[0, 2, 4, 5]`.
 *
 * 🔴 Congeladas aquí porque la lectura del env DESAPARECE en RET-1269 U8: no
 * queda ningún umbral de cancelación que publicar, así que un
 * `publicado ?? VITE_*` no tendría lado izquierdo y dejaría al front pegado a
 * la constante heredada que este trabajo existe para quitar. El contador nuevo
 * NO las consulta — sólo el resolver de los tres modales, que vive tras
 * `VITE_PAST_DUE_LADDER_FEATURE` y devuelve `null` en cuanto la escalera se
 * enciende.
 *
 * Se van las dos juntas —esta constante y el resolver— cuando el flag salga del
 * código, no antes: con el flag apagado son la única señal de cobranza que
 * alcanza al cliente sin que él navegue a Ajustes.
 */
export const LEGACY_PAST_DUE_MODAL_DAYS: readonly [
  number,
  number,
  number,
  number,
] = [0, 2, 4, 5];

export const SUBSCRIPTION_INITIAL_STATE: tSubscriptionContext = {
  subscription: INITIAL_SUBSCRIPTION_VALUES,
  getSummary: async () => {},
  getPlans: async () => {},
  markNotificationAsRead: () => false,
  shouldSeeNoPaidModal: false,
  shouldSeePrevPastDueModal: false,
  shouldSeePastDueModal: false,
  shouldSeeConfirmationModal: false,
  sendTrialConfirmation: async () => false,
  isSetUpAvailable: true,
  cancelCurrentSubscription: () => Promise.resolve(),
  addAddon: () => Promise.resolve(),
  isClient: false,
  limitedActionAlert: () => {},
  setSubscription: () => {},
  setUpInfo: undefined,
  handleFeatureUpdate: () => {},
  isLoadingSubscription: true,
  setIsLoadingSubscription: () => {},
  plans: null,
  // Arranca en false: sin fetch no hay datos, y el default honesto de «no sé»
  // es no.
  hasSubscriptionData: false,
  // Sin datos nadie es "no-cliente confirmado": los paywalls fallan abiertos
  // hasta que el summary real llegue (BUG-868).
  isKnownNonClient: false,
  isConfirmedNonClient: false,
  loadingPlans: true,
  // `null` es «no está en mora», que es el default honesto sin datos. Un 0
  // diría «primer día de mora» a cualquier consumidor montado fuera del
  // provider.
  daysPastDue: null,
};
